/**
 * Turns raw ethers / RPC errors into short, actionable admin-UI messages.
 * Prefers known custom-error selectors from PrivacyPortal / Factory / OZ,
 * then shortMessage / reason, then a truncated generic fallback.
 */

/** Known Solidity custom-error selectors → human-readable copy. */
const CUSTOM_ERROR_MESSAGES: Record<string, string> = {
  // ── OpenZeppelin Pausable ──────────────────────────────────────────────
  '0x8dfc202b': 'Portal must be paused before rescuing tokens. Use Pause Contract first.',
  '0xd93c0665': 'Portal is paused. Unpause it before continuing.',

  // ── OpenZeppelin AccessControl / Ownable (factory grantRole / revokeRole) ──
  '0xe2517d3f': 'Not authorized. Your wallet needs the factory admin role for this action.',
  '0x6697b232': 'AccessControl confirmation failed.',
  '0x118cdaa7': 'Not authorized (Ownable). Connect with the contract owner.',
  '0x1e4fbdf7': 'Invalid Ownable owner address.',

  // ── OpenZeppelin ERC20 / SafeERC20 (rescueERC20, fee sweeps) ───────────
  '0xe450d38c': 'Insufficient token balance in the portal for this rescue amount.',
  '0xfb8f41b2': 'Insufficient ERC-20 allowance.',
  '0x96c6fd1e': 'Invalid ERC-20 sender.',
  '0xec442f05': 'Invalid ERC-20 receiver.',
  '0x5274afe7': 'Token transfer failed (SafeERC20). Check the token and amount.',

  // ── PrivacyPortal admin / operator roles ───────────────────────────────
  '0x0c96eb08': 'Not authorized. Factory admin role required (pause, limits, rescue, fees sweep).',
  '0xd8c8ffc0': 'Not authorized. Factory operator role required (fees, enable/disable deposits).',

  // ── PrivacyPortal pause / soft-disable / blacklist / limits ────────────
  '0xdeeb6943': 'Deposits are paused (portal or factory).',
  '0x6022a9e7': 'Withdrawals are paused (portal or factory).',
  '0xfb291504': 'Deposits are disabled on this portal (setIsDepositEnabled).',
  '0xdaf49ab9': 'Address is blacklisted.',
  '0xc24b1b61': 'Amount is below the minimum deposit limit.',
  '0xd630062d': 'Amount exceeds the maximum deposit limit.',
  '0x0fdbcf37': 'Amount is below the minimum withdraw limit.',
  '0x9aae5367': 'Amount exceeds the maximum withdraw limit.',
  '0x401f33c6': 'Invalid limit configuration (e.g. min > max, or inconsistent limits).',

  // ── PrivacyPortal fees / rescue ────────────────────────────────────────
  '0x2c5211c6': 'Invalid amount (must be greater than zero).',
  '0xe6c4247b': 'Invalid address (zero address not allowed).',
  '0x1e5b1769': 'Insufficient accumulated portal fees for this amount.',
  '0x0c39c732': 'Cannot rescue the paired pToken from this portal.',
  '0x6d963f88': 'Native coin transfer failed (fee or rescue recipient may reject ETH/AVAX).',
  '0x01b1b7e3': 'Portal fee is below the required floor.',
  '0xa5659447': 'Portal fee exceeds the configured maximum.',
  '0xdcf6afcb': 'Incorrect fee paid (msg.value does not match expected fees).',

  // ── PrivacyPortalFeeLib (setDepositFee / setWithdrawFee packing) ───────
  '0x8181adca': 'Fee value overflows packed storage (fixed or max fee too large).',
  '0x6d24edae':
    'Invalid fee configuration. Max fee must be > 0 and ≥ fixed fee; percentage must be ≤ 10% (100000 units). Use 0 in the UI for “no cap” (maps to uint128.max on-chain).',

  // ── PrivacyPortal lifecycle / escrow (less common in backoffice) ───────
  '0xe5af51c1': 'Portal factory is not configured.',
  '0x69bd61eb': 'Native wrap is disabled on this portal.',
  '0x713f1086': 'Only the paired pToken may call this function.',
  '0x3294be46': 'Unknown withdrawal id.',
  '0xf412fa33': 'Withdrawal is not in a pending state.',
  '0x26dddf15': 'Pending burn amount is too low for this batch burn.',
  '0xc8ea6f59': 'pToken transfer for this withdrawal was not successful.',
  '0xd193f492': 'Deposit escrow is in an invalid state for this action.',
  '0x316c160d': 'Deposit mint has not failed — refund not available.',
  '0x5b044d68': 'Withdraw transfer has not failed — cancel not available.',

  // ── PrivacyPortalFactory ───────────────────────────────────────────────
  '0xe0006357': 'Not an allowlisted factory deployer.',
  '0x4fd43c92': 'A portal already exists for this underlying token.',
  '0x67e64154': 'Unknown pToken (not registered to this factory).',
  '0x04beb044': 'Price oracle is not configured on the factory.',
  '0xeb0f92be': 'Factory admin is not configured (no DEFAULT_ADMIN_ROLE holder).',
};

const PANIC_MESSAGES: Record<number, string> = {
  0x01: 'Solidity assert failed.',
  0x11: 'Arithmetic overflow/underflow.',
  0x12: 'Division or modulo by zero.',
  0x21: 'Converted value out of enum range.',
  0x22: 'Incorrectly encoded storage byte array.',
  0x31: 'pop() on an empty array.',
  0x32: 'Array index out of bounds.',
  0x41: 'Too much memory allocated.',
  0x51: 'Called a zero-initialized function pointer.',
};

function extractRevertDataHex(err: unknown): string | null {
  const e = err as {
    data?: unknown;
    info?: { error?: { data?: unknown } };
    error?: { data?: unknown };
    cause?: { data?: unknown };
  };

  const tryHex = (v: unknown): string | null => {
    if (typeof v === 'string' && /^0x[0-9a-fA-F]+$/.test(v) && v.length >= 10) {
      return v.toLowerCase();
    }
    if (v && typeof v === 'object' && 'data' in (v as object)) {
      return tryHex((v as { data: unknown }).data);
    }
    return null;
  };

  for (const candidate of [e.data, e.info?.error?.data, e.error?.data, e.cause?.data]) {
    const hex = tryHex(candidate);
    if (hex) return hex;
  }

  const msg =
    typeof (err as { message?: string })?.message === 'string'
      ? (err as { message: string }).message
      : '';
  const embedded = msg.match(/data="?(0x[0-9a-fA-F]+)"?/);
  return embedded ? embedded[1].toLowerCase() : null;
}

function messageForRevertData(data: string): string | null {
  if (!data.startsWith('0x') || data.length < 10) return null;

  const selector = data.slice(0, 10);
  if (CUSTOM_ERROR_MESSAGES[selector]) return CUSTOM_ERROR_MESSAGES[selector];

  // Panic(uint256) — 0x4e487b71 + 32-byte code
  if (selector === '0x4e487b71' && data.length >= 74) {
    const code = Number(BigInt('0x' + data.slice(10, 74)));
    return PANIC_MESSAGES[code] ?? `Contract panic (code ${code}).`;
  }

  // Error(string) — 0x08c379a0
  if (selector === '0x08c379a0' && data.length >= 138) {
    try {
      // offset (32) + length (32) + string
      const len = Number(BigInt('0x' + data.slice(74, 138)));
      if (len > 0 && len < 500) {
        const strHex = data.slice(138, 138 + len * 2);
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = parseInt(strHex.slice(i * 2, i * 2 + 2), 16);
        }
        const decoded = new TextDecoder().decode(bytes).replace(/\0/g, '').trim();
        if (decoded) return decoded;
      }
    } catch {
      // fall through
    }
  }

  return null;
}

function isUserRejection(err: unknown): boolean {
  const e = err as { code?: string | number; shortMessage?: string; message?: string };
  if (e.code === 'ACTION_REJECTED' || e.code === 4001) return true;
  const text = `${e.shortMessage ?? ''} ${e.message ?? ''}`.toLowerCase();
  return text.includes('user rejected') || text.includes('user denied') || text.includes('rejected the request');
}

export function formatAdminError(err: unknown, fallback = 'Transaction failed'): string {
  if (err == null) return fallback;

  if (isUserRejection(err)) {
    return 'Transaction rejected in wallet.';
  }

  const revertData = extractRevertDataHex(err);
  if (revertData) {
    const mapped = messageForRevertData(revertData);
    if (mapped) return mapped;
  }

  const e = err as {
    shortMessage?: string;
    reason?: string;
    message?: string;
    info?: { error?: { message?: string } };
  };

  const candidates = [
    e.reason,
    e.shortMessage,
    e.info?.error?.message,
    e.message,
  ].filter((v): v is string => typeof v === 'string' && v.trim().length > 0);

  for (const raw of candidates) {
    const quoted = raw.match(/execution reverted(?::\s*|\s+)["']([^"']+)["']/i);
    if (quoted?.[1]) return quoted[1].trim();

    const ownable = raw.match(/Ownable:\s*[^(,\n]+/i);
    if (ownable) return ownable[0].trim();

    if (/unknown custom error/i.test(raw) && revertData) {
      return `Contract reverted (${revertData.slice(0, 10)}).`;
    }

    // Prefer shortMessage-style lines without embedded calldata
    if (!raw.includes('data="0x') && !raw.includes('data=0x') && raw.length <= 200) {
      return raw.replace(/^Error:\s*/i, '').trim();
    }
  }

  if (revertData) {
    return `Contract reverted (${revertData.slice(0, 10)}).`;
  }

  const msg = candidates[0] ?? fallback;
  const cleaned = msg.replace(/^Error:\s*/i, '').trim();
  return cleaned.length > 220 ? `${cleaned.slice(0, 220)}…` : cleaned;
}
